/**
 * One campaign: written and previewed while a draft, then sent and watched.
 *
 * The preview is the server rendering the message for the first person in the audience with the same
 * code the sender uses, so what is on screen is what goes out - footer and unsubscribe link included.
 * Sending is an administrator's, and the confirmation names how many people it reaches; once it has
 * started the campaign is frozen (duplicate it to send a changed copy), and the page polls while the
 * paced sender works through the list.
 */
import { TextArea, type Container } from 'webwin';
import { isAdmin } from '../api/auth.js';
import { describeError } from '../api/http.js';
import * as marketing from '../api/marketing.js';
import {
  CampaignRecipientOutcome,
  CampaignStatus,
  type CampaignDetailDto,
  type CampaignRecipientDto,
  type SaveCampaignRequest
} from '../contracts.gen.js';
import { join, localDateTime, plural } from '../format.js';
import { campaignTone, LEAD_STATUSES, outcomeTone, statusLabel } from '../leads.js';
import type { Card } from '../ui/Card.js';
import { CardList } from '../ui/CardList.js';
import { chip } from '../ui/Chip.js';
import { confirm } from '../ui/dialogs.js';
import { link } from '../ui/Link.js';
import { StatusLine } from '../ui/StatusLine.js';
import type { CheckBox } from 'webwin';
import {
  button,
  buttonRow,
  card,
  checkBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  inlineRow,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

export function campaignDetailPage(): CrmPage {
  return {
    async mount(host: Container, ctx: PageContext) {
      const id = Number(ctx.params.id);
      const admin = isAdmin();
      let detail: CampaignDetailDto | null = null;
      let busy = false;
      let polling = false;

      const status = new StatusLine();
      const title = h1('Campaign');
      const titleChip = inlineRow();
      const actions = buttonRow();
      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        link('← Marketing', () => ctx.go('/marketing'), 'brand'),
        stackOf(vstack({ spacing: GAP.xs }), title, titleChip),
        actions,
        status.label
      );

      // ---- the draft editor ----
      const nameBox = textBox();
      const subjectBox = textBox();
      const bodyBox = new TextArea({ height: 240, hAlign: 'stretch', wrap: true });
      const includeLeads = checkBox('Leads', true);
      const statusChecks: CheckBox[] = LEAD_STATUSES.map(s => checkBox(statusLabel(s), false));
      const tagBox = textBox({ placeholder: 'Only leads with this tag (optional)' });
      const includeContacts = checkBox('Customers’ billing contacts', false);
      const includeEmployees = checkBox('Customers’ employees', false);
      const editorLine = new StatusLine();
      const editorCard = card();
      editorCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Message'),
          fieldRow(field('Name (only staff see this)', nameBox), field('Subject', subjectBox)),
          field('Body', bodyBox),
          muted(
            'Placeholders: {FirstName}, {Name}, {Company}, {Email}, {Sender}, {UnsubscribeLink}. Your postal address and an unsubscribe link are added to the foot of every message whatever the body says.'
          ),
          h2('Audience'),
          includeLeads,
          muted('Lead statuses (none ticked = every open lead; Won and Lost only when ticked):'),
          stackOf(buttonRow({ spacing: GAP.md }), ...statusChecks),
          tagBox,
          stackOf(buttonRow({ spacing: GAP.lg }), includeContacts, includeEmployees),
          muted('Everyone is de-duplicated by address, and anyone on the do-not-email list is left out.'),
          stackOf(buttonRow(), button('Save', () => void save(), true), button('Preview', () => void preview())),
          editorLine.label
        )
      );

      // ---- preview ----
      const previewCount = text('', { font: FONT.bold });
      const previewBlocked = muted('', 'danger');
      const previewWarning = muted('', 'warn');
      const previewSample = muted('');
      const previewSubject = text('', { font: FONT.bold });
      const previewBody = text('');
      const previewCard = card({ visible: false, tint: 'alt' });
      previewCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          h2('Preview'),
          previewCount,
          previewBlocked,
          previewWarning,
          previewSample,
          previewSubject,
          previewBody
        )
      );

      // ---- the sent view ----
      const summary = text('');
      const recipients = new CardList<CampaignRecipientDto>({ key: r => r.id, build: r => recipientCard(r), max: 500 });
      const sentCard = card({ visible: false });
      sentCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), h2('Delivery'), summary));
      const messageCard = card({ visible: false });
      const sentSubject = text('', { font: FONT.bold });
      const sentBody = text('');
      messageCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), h2('Message'), sentSubject, sentBody));
      const recipientsHeading = h2('Recipients');

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.lg }),
          header,
          editorCard,
          previewCard,
          sentCard,
          messageCard,
          recipientsHeading,
          recipients.panel
        )
      );
      host.controls.add(view);

      function recipientCard(r: CampaignRecipientDto): Card {
        const row = splitRow({ align: 'center' });
        const left = stackOf(vstack({ spacing: 0 }), value(r.name ? `${r.name} <${r.email}>` : r.email, { font: FONT.bold }));
        const line = join([r.kindLabel, r.sentUtc ? localDateTime(r.sentUtc) : null, r.unsubscribed ? 'unsubscribed' : null, r.error]);
        if (line) left.controls.add(muted(line, r.outcome === CampaignRecipientOutcome.Failed ? 'danger' : 'muted'));
        fillWith(row, left);
        row.controls.add(chip(r.outcomeLabel, outcomeTone(r.outcome)));
        const shell = card();
        shell.controls.add(row);
        return shell;
      }

      function request(): SaveCampaignRequest {
        const statuses = LEAD_STATUSES.filter((_, i) => statusChecks[i]?.checked);
        return {
          name: nameBox.text.trim(),
          subject: subjectBox.text.trim(),
          body: bodyBox.text,
          audience: {
            includeLeads: includeLeads.checked,
            leadStatuses: statuses,
            leadTag: tagBox.text.trim(),
            includeCustomerContacts: includeContacts.checked,
            includeEmployees: includeEmployees.checked,
            customerIds: []
          }
        };
      }

      function render(d: CampaignDetailDto): void {
        detail = d;
        const c = d.campaign;
        title.text = c.name;
        titleChip.controls.clear();
        titleChip.controls.add(chip(c.statusLabel, campaignTone(c.status)));
        const draft = c.status === CampaignStatus.Draft;

        actions.controls.clear();
        if (draft) {
          if (admin) actions.controls.add(button('Send…', () => void send(), true));
          actions.controls.add(button('Delete draft', () => void remove()));
        }
        if (c.status === CampaignStatus.Sending && admin) actions.controls.add(button('Stop sending', () => void cancel()));
        actions.controls.add(button('Duplicate', () => void duplicate()));

        editorCard.visible = draft;
        if (draft && !busy) {
          nameBox.text = c.name ?? '';
          subjectBox.text = c.subject ?? '';
          bodyBox.text = c.body ?? '';
          const a = c.audience;
          includeLeads.checked = a.includeLeads;
          LEAD_STATUSES.forEach((s, i) => {
            const box = statusChecks[i];
            if (box) box.checked = (a.leadStatuses ?? []).includes(s);
          });
          tagBox.text = a.leadTag ?? '';
          includeContacts.checked = a.includeCustomerContacts;
          includeEmployees.checked = a.includeEmployees;
        }
        if (!draft) previewCard.visible = false;

        sentCard.visible = !draft;
        messageCard.visible = !draft;
        recipientsHeading.visible = !draft;
        recipients.panel.visible = !draft;
        if (!draft) {
          summary.text = join(
            [
              `${c.sent} of ${plural(c.recipients, 'recipient')} sent`,
              c.failed > 0 ? `${c.failed} failed` : null,
              c.skipped > 0 ? `${c.skipped} skipped` : null,
              c.pending > 0 ? `${c.pending} waiting (one every couple of seconds)` : null,
              c.unsubscribes > 0 ? plural(c.unsubscribes, 'unsubscribe') : null,
              c.leadsCreated > 0 ? `${plural(c.leadsCreated, 'lead')} from it, ${c.leadsWon} won` : null,
              c.startedUtc ? `started ${localDateTime(c.startedUtc)} by ${c.startedBy}` : null,
              c.finishedUtc ? `finished ${localDateTime(c.finishedUtc)}` : null
            ],
            ' · '
          );
          sentSubject.text = c.subject;
          sentBody.text = c.body;
          recipients.set(d.recipients);
        }
        relayout(content);

        if (c.status === CampaignStatus.Sending && !polling) {
          polling = true;
          ctx.dispose.timeout(() => {
            polling = false;
            void load();
          }, 5000);
        }
      }

      async function load(): Promise<void> {
        try {
          const d = await marketing.getCampaign(id, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          render(d);
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      async function save(): Promise<boolean> {
        if (busy) return false;
        const req = request();
        if (!req.name) {
          editorLine.error('Give the campaign a name.');
          return false;
        }
        busy = true;
        editorLine.busy('Saving…');
        try {
          await marketing.updateCampaign(id, req);
          if (ctx.cancelled()) return false;
          editorLine.ok('Saved.');
          busy = false;
          await load();
          return true;
        } catch (error) {
          if (!ctx.cancelled()) editorLine.error(describeError(error));
          return false;
        } finally {
          busy = false;
        }
      }

      async function preview(): Promise<void> {
        if (busy) return;
        busy = true;
        editorLine.busy('Rendering the preview…');
        try {
          const p = await marketing.previewCampaign(request(), ctx.dispose.signal());
          if (ctx.cancelled()) return;
          previewCount.text =
            `Would go to ${plural(p.recipients, 'person', 'people')}` +
            (p.suppressed > 0 ? ` (${p.suppressed} more left out: on the do-not-email list).` : '.');
          previewBlocked.text = p.blocked ?? '';
          previewBlocked.visible = !!p.blocked;
          previewWarning.text = p.warning ?? '';
          previewWarning.visible = !!p.warning;
          previewSample.text = p.sample.length
            ? 'First: ' + p.sample.map(s => (s.name ? `${s.name} <${s.email}>` : s.email) + ` (${s.kindLabel})`).join(', ')
            : '';
          previewSample.visible = p.sample.length > 0;
          previewSubject.text = 'Subject: ' + (p.renderedSubject || '(no subject)');
          previewBody.text = p.renderedBody;
          previewCard.visible = true;
          editorLine.clear();
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) editorLine.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function send(): Promise<void> {
        if (busy || !detail) return;
        if (!(await save())) return;
        let p;
        try {
          p = await marketing.previewCampaign(request(), ctx.dispose.signal());
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
          return;
        }
        if (ctx.cancelled()) return;
        if (p.blocked) {
          status.error(p.blocked);
          return;
        }
        const ok = await confirm(ctx.desktop, {
          title: 'Send campaign',
          message:
            `Email "${subjectBox.text.trim() || '(no subject)'}" to ${plural(p.recipients, 'person', 'people')} now? ` +
            'It cannot be called back once a message has gone; stopping only holds the ones not yet sent.' +
            (p.warning ? ` ${p.warning}` : '')
        });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        status.busy('Starting…');
        try {
          const c = await marketing.sendCampaign(id);
          if (ctx.cancelled()) return;
          status.ok(`Sending to ${plural(c.recipients, 'person', 'people')}.`);
          busy = false;
          await load();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function cancel(): Promise<void> {
        if (busy) return;
        const ok = await confirm(ctx.desktop, {
          title: 'Stop sending',
          message: 'Stop the campaign? Messages already sent stay sent; the rest are not sent.'
        });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        try {
          await marketing.cancelCampaign(id);
          if (ctx.cancelled()) return;
          status.ok('Stopped.');
          busy = false;
          await load();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function duplicate(): Promise<void> {
        if (busy) return;
        busy = true;
        try {
          const c = await marketing.duplicateCampaign(id);
          ctx.go(`/marketing/${c.id}`);
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function remove(): Promise<void> {
        if (busy || !detail) return;
        const ok = await confirm(ctx.desktop, { title: 'Delete draft', message: `Delete the draft "${detail.campaign.name}"?` });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        try {
          await marketing.deleteCampaign(id);
          ctx.go('/marketing', { replace: true });
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      await load();
    }
  };
}
